// Lets any component ask for the floating bubbles (Reports / Record / Chat) to
// be hidden while it is on screen.
//
// They are mounted globally in App.js and sit at a high z-index in the bottom
// corner, where they cover a modal's footer buttons. The modal cannot reach
// App's render, so the request travels through this counter instead.
//
// A COUNTER, not a boolean: two things can want them hidden at once (a modal
// that opens another), and the first one to close must not reveal them while
// the second is still up. They come back when the last request is released.
import { useEffect, useState } from "react";

let hideRequests = 0;
const listeners = new Set();

function notify() {
  for (const fn of listeners) fn(hideRequests > 0);
}

/** Ask for the bubbles to be hidden. Returns the matching release fn. */
export function requestHideFloatingBubbles() {
  hideRequests += 1;
  notify();
  let released = false;
  return () => {
    // Guarded: a double release would drop someone else's request and pop the
    // bubbles back over a modal that is still open.
    if (released) return;
    released = true;
    hideRequests = Math.max(0, hideRequests - 1);
    notify();
  };
}

/** True while anything has asked for the bubbles to be hidden. */
export function useFloatingBubblesHidden() {
  const [hidden, setHidden] = useState(hideRequests > 0);
  useEffect(() => {
    listeners.add(setHidden);
    setHidden(hideRequests > 0);
    return () => listeners.delete(setHidden);
  }, []);
  return hidden;
}

/** Hide the bubbles for as long as `active` is true. */
export function useHideFloatingBubbles(active) {
  useEffect(() => {
    if (!active) return undefined;
    return requestHideFloatingBubbles();
  }, [active]);
}

/** Test seam: forget every outstanding request. */
export function __resetFloatingBubbles() {
  hideRequests = 0;
  listeners.clear();
}
